/**
 * Canonical operation IDs
 *
 * Stable, dot-notation operation identifiers used by controllers, guards,
 * audit, telemetry, and generated contracts. These are the single source of
 * truth for operation identity.
 */
// ──── Auth Operations ────
export const AUTH_LOGIN = 'auth.login';
export const AUTH_LOGOUT = 'auth.logout';
export const AUTH_REFRESH = 'auth.refresh';
export const AUTH_CHALLENGE_TOTP = 'auth.challenge.totp';
export const AUTH_CHALLENGE_RECOVERY = 'auth.challenge.recovery';
export const AUTH_SELECT_CONTEXT = 'auth.select.context';
export const AUTH_PASSWORD_FORGOT = 'auth.password.forgot';
export const AUTH_PASSWORD_RESET = 'auth.password.reset';
export const AUTH_INVITATION_ACCEPT = 'auth.invitation.accept';
export const AUTH_PASSWORD_CHANGE = 'auth.password.change';
export const AUTH_SESSION_MANAGE = 'auth.sessions.manage';
export const AUTH_SESSION_REVOKE = 'auth.sessions.revoke';
export const AUTH_ENROLL_MFA = 'auth.mfa.enroll';
export const AUTH_VERIFY_MFA = 'auth.mfa.verify';
export const AUTH_DISABLE_MFA = 'auth.mfa.disable';
export const AUTH_REGENERATE_BACKUP_CODES = 'auth.mfa.backupCodes.regenerate';
export const AUTH_READ_PROFILE = 'auth.readProfile';
export const AUTH_SWITCH_CONTEXT = 'auth.switchContext';
export const AUTH_ENTER_DELEGATION = 'auth.delegation.enter';
export const AUTH_EXIT_DELEGATION = 'auth.delegation.exit';
// ──── Platform Operations ────
export const PLATFORM_HEALTH_CHECK = 'platform.health.check';
export const PLATFORM_HEALTH_READINESS = 'platform.health.readiness';
export const PLATFORM_HEALTH_LIVENESS = 'platform.health.liveness';
export const PLATFORM_METRICS_READ = 'platform.metrics.read';
export const PLATFORM_METRICS_READ_JSON = 'platform.metrics.read-json';
export const PLATFORM_ORGANIZATIONS_LIST = 'platform.organizations.list';
export const PLATFORM_ORGANIZATIONS_READ = 'platform.organizations.read';
export const PLATFORM_ORGANIZATIONS_CREATE = 'platform.organizations.create';
export const PLATFORM_ORGANIZATIONS_UPDATE = 'platform.organizations.update';
export const PLATFORM_ORGANIZATIONS_DISABLE = 'platform.organizations.disable';
export const PLATFORM_ORGANIZATIONS_SUPPORT_COMMAND_ISSUE = 'platform.organizations.support-command.issue';
export const PLATFORM_ORGANIZATIONS_ONBOARDING_READINESS_READ = 'platform.organizations.onboarding-readiness.read';
export const PLATFORM_ORGANIZATIONS_OFFBOARDING_EXPORT = 'platform.organizations.offboarding-export';
export const PLATFORM_MODULE_ENABLE = 'platform.module.enable';
export const PLATFORM_MODULE_DISABLE = 'platform.module.disable';
export const PLATFORM_ENTITLEMENTS_LIST = 'platform.entitlements.list';
export const PLATFORM_MODULES_LIST = 'platform.modules.list';
export const PLATFORM_MODULES_READ = 'platform.modules.read';
export const PLATFORM_MODULES_ASSIGN = 'platform.modules.assign';
export const PLATFORM_MODULES_REVOKE = 'platform.modules.revoke';
export const PLATFORM_MODULES_CATALOG_LIST = 'platform.modules.catalog.list';
export const PLATFORM_MODULES_CATALOG_MANAGE = 'platform.modules.catalog.manage';
export const PLATFORM_MODULES_ASSIGN_TO_ORGANIZATION = 'platform.modules.assign-to-organization';
export const PLATFORM_MODULES_REVOKE_FROM_ORGANIZATION = 'platform.modules.revoke-from-organization';
export const ORGANIZATION_MODULES_LIST_ASSIGNED = 'organization.modules.list-assigned';
export const ORGANIZATION_MODULES_ENABLE = 'organization.modules.enable';
export const ORGANIZATION_MODULES_DISABLE = 'organization.modules.disable';
export const ORGANIZATION_MODULES_CONFIGURE = 'organization.modules.configure';
export const ORGANIZATION_USERS_LIST = 'organization.users.list';
export const ORGANIZATION_USERS_READ = 'organization.users.read';
export const ORGANIZATION_USERS_CREATE = 'organization.users.create';
export const ORGANIZATION_USERS_UPDATE = 'organization.users.update';
export const ORGANIZATION_USERS_DISABLE = 'organization.users.disable';
export const ORGANIZATION_USERS_UNLOCK = 'organization.users.unlock';
export const ORGANIZATION_USERS_FORCE_PASSWORD_RESET = 'organization.users.force-password-reset';
export const ORGANIZATION_USERS_MFA_REQUIRE_ENROLLMENT = 'organization.users.mfa.require-enrollment';
export const ORGANIZATION_USERS_MFA_RESET_ENROLLMENT = 'organization.users.mfa.reset-enrollment';
export const ORGANIZATION_USERS_MFA_DISABLE = 'organization.users.mfa.disable';
export const ORGANIZATION_USERS_MFA_REGENERATE_RECOVERY = 'organization.users.mfa.regenerate-recovery';
export const ORGANIZATION_ROLES_LIST = 'organization.roles.list';
export const ORGANIZATION_ROLES_READ = 'organization.roles.read';
export const ORGANIZATION_ROLES_CREATE = 'organization.roles.create';
export const ORGANIZATION_ROLES_UPDATE = 'organization.roles.update';
export const ORGANIZATION_ROLES_DISABLE = 'organization.roles.disable';
export const ORGANIZATION_PERMISSIONS_LIST = 'organization.permissions.list';
export const ORGANIZATION_PERMISSION_ASSIGNMENTS_LIST = 'organization.permission-assignments.list';
export const ORGANIZATION_PERMISSION_ASSIGNMENTS_EDIT = 'organization.permission-assignments.edit';
export const SETTLEMENT_INSTRUCTIONS_LIST = 'settlement.instructions.list';
export const SETTLEMENT_FAILURES_LIST = 'settlement.failures.list';
export const SETTLEMENT_INSTRUCTIONS_GET = 'settlement.instructions.get';
export const SETTLEMENT_INSTRUCTIONS_CREATE = 'settlement.instructions.create';
export const SETTLEMENT_INSTRUCTIONS_CONFIRM = 'settlement.instructions.confirm';
export const SETTLEMENT_INSTRUCTIONS_CORRECT = 'settlement.instructions.correct';
export const SETTLEMENT_INSTRUCTIONS_CANCEL = 'settlement.instructions.cancel';
export const SETTLEMENT_INSTRUCTIONS_AVAILABILITY = 'settlement.instructions.availability';
export const POSITION_TRANSFERS_LIST = 'investment.position-transfer.list';
export const POSITION_TRANSFERS_GET = 'investment.position-transfer.get';
export const POSITION_TRANSFERS_ELIGIBILITY = 'investment.position-transfer.eligibility';
export const POSITION_TRANSFERS_REQUEST_ELIGIBILITY = 'investment.position-transfer.request-eligibility';
export const POSITION_TRANSFERS_REQUEST = 'investment.position-transfer.request';
export const POSITION_TRANSFERS_APPROVE = 'investment.position-transfer.approve';
export const POSITION_TRANSFERS_EXECUTE = 'investment.position-transfer.execute';
export const POSITION_TRANSFERS_CANCEL = 'investment.position-transfer.cancel';
export const POSITION_TRANSFERS_REVERSE = 'investment.position-transfer.reverse';
export const ORGANIZATION_BRANCHES_LIST = 'organization.branches.list';
export const ORGANIZATION_BRANCHES_READ = 'organization.branches.read';
export const ORGANIZATION_BRANCHES_CREATE = 'organization.branches.create';
export const ORGANIZATION_BRANCHES_UPDATE = 'organization.branches.update';
export const ORGANIZATION_BRANCHES_DISABLE = 'organization.branches.disable';
export const ORGANIZATION_BRANCHES_MAKE_DEFAULT = 'organization.branches.make-default';
export const ORGANIZATION_CLIENTS_LIST = 'organization.clients.list';
export const ORGANIZATION_CLIENTS_READ = 'organization.clients.read';
export const ORGANIZATION_CLIENTS_CREATE = 'organization.clients.create';
export const ORGANIZATION_CLIENTS_UPDATE = 'organization.clients.update';
export const ORGANIZATION_CLIENTS_DISABLE = 'organization.clients.disable';
export const ORGANIZATION_CLIENT_ACCOUNTS_LIST = 'organization.client-accounts.list';
export const ORGANIZATION_CLIENT_ACCOUNTS_READ = 'organization.client-accounts.read';
export const ORGANIZATION_CLIENT_ACCOUNTS_CREATE = 'organization.client-accounts.create';
export const ORGANIZATION_CLIENT_ACCOUNTS_UPDATE = 'organization.client-accounts.update';
export const ORGANIZATION_CLIENT_ACCOUNTS_DISABLE = 'organization.client-accounts.disable';
export const ORGANIZATION_CLIENT_ACCOUNT_DOCUMENTS_LIST = 'organization.client-accounts.documents.list';
export const ORGANIZATION_CLIENT_ACCOUNT_DOCUMENTS_CREATE = 'organization.client-accounts.documents.create';
export const ORGANIZATION_CLIENT_ACCOUNT_DOCUMENTS_REMOVE = 'organization.client-accounts.documents.remove';
export const ORGANIZATION_CLIENT_ACCOUNT_SIGNATORIES_LIST = 'organization.client-accounts.signatories.list';
export const ORGANIZATION_CLIENT_ACCOUNT_SIGNATORIES_CREATE = 'organization.client-accounts.signatories.create';
export const ORGANIZATION_CLIENT_ACCOUNT_SIGNATORIES_REMOVE = 'organization.client-accounts.signatories.remove';
export const ORGANIZATION_PORTFOLIOS_LIST = 'organization.portfolios.list';
export const ORGANIZATION_PORTFOLIOS_READ = 'organization.portfolios.read';
export const ORGANIZATION_PORTFOLIOS_CREATE = 'organization.portfolios.create';
export const ORGANIZATION_PORTFOLIOS_UPDATE = 'organization.portfolios.update';
export const ORGANIZATION_PORTFOLIOS_DISABLE = 'organization.portfolios.disable';
export const ORGANIZATION_BENCHMARK_ASSIGNMENTS_LIST = 'organization.benchmark-assignments.list';
export const ORGANIZATION_BENCHMARK_ASSIGNMENTS_CREATE = 'organization.benchmark-assignments.create';
export const ORGANIZATION_BENCHMARK_ASSIGNMENTS_APPROVE = 'organization.benchmark-assignments.approve';
export const CLIENT_DASHBOARD_READ = 'client.dashboard.read';
export const CLIENT_PROFILE_READ = 'client.profile.read';
export const CLIENT_PROFILE_UPDATE_LIMITED = 'client.profile.update-limited';
export const CLIENT_HOLDINGS_READ_OWN = 'client.holdings.read-own';
export const CLIENT_TRANSACTIONS_READ_OWN = 'client.transactions.read-own';
export const CLIENT_REPORTS_READ_OWN = 'client.reports.read-own';
export const CLIENT_REPORTS_EXPORT_OWN = 'client.reports.export-own';
export const CLIENT_DOCUMENTS_READ_OWN = 'client.documents.read-own';
export const PLATFORM_LICENSE_CREATE = 'platform.license.create';
export const PLATFORM_LICENSE_LIST = 'platform.license.list';
export const PLATFORM_USERS_LIST = 'platform.users.list';
export const PLATFORM_USERS_READ = 'platform.users.read';
export const PLATFORM_USERS_CREATE = 'platform.users.create';
export const PLATFORM_USERS_UPDATE = 'platform.users.update';
export const PLATFORM_USERS_DISABLE = 'platform.users.disable';
export const PLATFORM_AUDIT_LIST = 'platform.audit.list';
export const PLATFORM_AUDIT_READ = 'platform.audit.read';
export const PLATFORM_AUDIT_EXPORT = 'platform.audit.export';
export const PLATFORM_REPORT_SNAPSHOT_CREATE = 'platform.reports.snapshot.create';
export const PLATFORM_REPORT_RESTATEMENT_CREATE = 'platform.reports.restatement.create';
export const PLATFORM_CONFIGURATION_ROLLOUT_MANAGE = 'platform.configuration-rollout.manage';
export const PLATFORM_CONFIGURATION_ROLLOUT_READ = 'platform.configuration-rollout.read';
export const PLATFORM_DATA_RESIDENCY_READ = 'platform.data-residency.read';
export const PLATFORM_DATA_RESIDENCY_POLICY_UPDATE = 'platform.data-residency.policy.update';
export const PLATFORM_DATA_RESIDENCY_TRANSFER_EVALUATE = 'platform.data-residency.transfer.evaluate';
export const PLATFORM_DATA_RESIDENCY_EXCEPTION_APPROVE = 'platform.data-residency.exception.approve';
export const PLATFORM_JURISDICTION_PACKAGES_READ = 'platform.jurisdiction-packages.read';
export const PLATFORM_JURISDICTION_PACKAGES_MANAGE = 'platform.jurisdiction-packages.manage';
export const PLATFORM_REFERENCE_MASTER_READ = 'platform.reference-master.read';
export const PLATFORM_REFERENCE_MASTER_MANAGE = 'platform.reference-master.manage';
export const PLATFORM_MARKET_DATA_QUALITY_READ = 'platform.market-data-quality.read';
export const PLATFORM_MARKET_DATA_QUALITY_MANAGE = 'platform.market-data-quality.manage';
export const PLATFORM_MARKET_DATA_QUALITY_APPROVE = 'platform.market-data-quality.approve';
export const PLATFORM_PROVIDER_INGESTION_READ = 'platform.provider-ingestion.read';
export const PLATFORM_PROVIDER_INGESTION_MANAGE = 'platform.provider-ingestion.manage';
export const PLATFORM_ISSUER_EVENTS_READ = 'platform.issuer-events.read';
export const PLATFORM_ISSUER_EVENTS_MANAGE = 'platform.issuer-events.manage';
export const PLATFORM_ISSUER_EVENTS_APPROVE = 'platform.issuer-events.approve';
export const PLATFORM_PERMISSION_CATALOG_MIGRATIONS_READ = 'platform.permission-catalog-migrations.read';
export const PLATFORM_PERMISSION_CATALOG_MIGRATIONS_MANAGE = 'platform.permission-catalog-migrations.manage';
export const PLATFORM_FEDERATION_READ = 'platform.federation.read';
export const PLATFORM_FEDERATION_MANAGE = 'platform.federation.manage';
export const PLATFORM_SERVICE_ACCOUNTS_READ = 'platform.service-accounts.read';
export const PLATFORM_SERVICE_ACCOUNTS_MANAGE = 'platform.service-accounts.manage';
export const PLATFORM_ACCESS_REVIEWS_READ = 'platform.access-reviews.read';
export const PLATFORM_ACCESS_REVIEWS_MANAGE = 'platform.access-reviews.manage';
export const PLATFORM_CRYPTOGRAPHIC_KEYS_READ = 'platform.cryptographic-keys.read';
export const PLATFORM_CRYPTOGRAPHIC_KEYS_MANAGE = 'platform.cryptographic-keys.manage';
export const PLATFORM_SCHEDULED_CONTROLS_READ = 'platform.scheduled-controls.read';
export const PLATFORM_SCHEDULED_CONTROLS_MANAGE = 'platform.scheduled-controls.manage';
export const PLATFORM_SCHEDULED_CONTROLS_APPROVE = 'platform.scheduled-controls.approve';
export const PLATFORM_SECURITY_INCIDENTS_READ = 'platform.security-incidents.read';
export const PLATFORM_SECURITY_INCIDENTS_MANAGE = 'platform.security-incidents.manage';
export const PLATFORM_ORGANIZATION_OWNERSHIP_READ = 'platform.organization-ownership.read';
export const PLATFORM_ORGANIZATION_OWNERSHIP_REQUEST = 'platform.organization-ownership.request';
export const PLATFORM_ORGANIZATION_OWNERSHIP_APPROVE = 'platform.organization-ownership.approve';
export const PLATFORM_MIGRATION_GOVERNANCE_READ = 'platform.migration-governance.read';
export const PLATFORM_MIGRATION_GOVERNANCE_REQUEST = 'platform.migration-governance.request';
export const PLATFORM_MIGRATION_GOVERNANCE_APPROVE = 'platform.migration-governance.approve';
export const PLATFORM_ORGANIZATION_PORTABILITY_READ = 'platform.organization-portability.read';
export const PLATFORM_ORGANIZATION_PORTABILITY_REQUEST = 'platform.organization-portability.request';
export const PLATFORM_ORGANIZATION_PORTABILITY_APPROVE = 'platform.organization-portability.approve';
export const PLATFORM_ORGANIZATION_PORTABILITY_PURGE = 'platform.organization-portability.purge';
export const PLATFORM_NOTIFICATION_GOVERNANCE_READ = 'platform.notification-governance.read';
export const PLATFORM_NOTIFICATION_GOVERNANCE_MANAGE = 'platform.notification-governance.manage';
export const PLATFORM_NOTIFICATION_GOVERNANCE_APPROVE = 'platform.notification-governance.approve';
export const PLATFORM_SUPPORT_DELEGATIONS_LIST = 'platform.support-delegations.list';
export const PLATFORM_SUPPORT_DELEGATIONS_READ = 'platform.support-delegations.read';
export const PLATFORM_SUPPORT_DELEGATIONS_CREATE = 'platform.support-delegations.create';
export const PLATFORM_SUPPORT_DELEGATIONS_APPROVE = 'platform.support-delegations.approve';
export const PLATFORM_SUPPORT_DELEGATIONS_REVOKE = 'platform.support-delegations.revoke';
export const ORGANIZATION_PROFILE_READ = 'organization.profile.read';
export const ORGANIZATION_PROFILE_DETAIL_READ = 'organization.profile.detail.read';
export const ORGANIZATION_PROFILE_UPDATE = 'organization.profile.update';
export const ORGANIZATION_AUDIT_LIST = 'organization.audit.list';
export const ORGANIZATION_AUDIT_READ = 'organization.audit.read';
export const ORGANIZATION_AUDIT_EXPORT = 'organization.audit.export';
// ORG-011: Dedicated inspector operation tied to the organization-scoped inspector permission.
export const ORGANIZATION_ACCESS_INSPECTOR = 'organization.access-inspector.read';
export const ORGANIZATION_LEGAL_HOLD_PLACE = 'organization.legal-hold.place';
export const ORGANIZATION_LEGAL_HOLD_REMOVE = 'organization.legal-hold.remove';
export const ORGANIZATION_LEGAL_HOLD_LIST = 'organization.legal-hold.list';
export const ORGANIZATION_ADMIN_DELEGATION_CREATE = 'organization.admin-delegation.create';
export const ORGANIZATION_ADMIN_DELEGATION_REVOKE = 'organization.admin-delegation.revoke';
export const ORGANIZATION_ADMIN_DELEGATION_LIST = 'organization.admin-delegation.list';
export const ORGANIZATION_REGULATORY_PROFILE_READ = 'organization.regulatory-profile.read';
export const ORGANIZATION_REGULATORY_PROFILE_UPDATE = 'organization.regulatory-profile.update';
export const NAVIGATION_READ = 'navigation.read';
export const NAVIGATION_HUB = 'navigation.hub';
// NAV-005: Unique operation IDs for each navigation endpoint
export const NAVIGATION_SHELL_READ = 'navigation.shell.read';
export const NAVIGATION_ROUTES_READ = 'navigation.routes.read';
export const NAVIGATION_ROUTE_READ = 'navigation.route.read';
export const NAVIGATION_HUB_READ = 'navigation.hub.read';
// ─── Experience / View Operations ───
export const DASHBOARD_VIEW = 'dashboard.view';
export const HOLDINGS_VIEW = 'holdings.view';
export const REPORTS_VIEW = 'reports.view';
export const REPORT_DETAIL_READ = 'reports.detail.read';
export const RECONCILIATION_VIEW = 'reconciliation.view';
export const CORPORATE_ACTIONS_VIEW = 'corporateActions.view';
export const CLIENTS_LIST = 'investment.clients.list';
export const CLIENTS_GET = 'investment.clients.get';
export const PORTFOLIOS_LIST = 'investment.portfolios.list';
export const PORTFOLIOS_GET = 'investment.portfolios.get';
export const POSITIONS_LIST = 'investment.positions.list';
export const POSITIONS_GET = 'investment.positions.get';
export const CASH_POSITIONS_LIST = 'investment.cash.list';
export const CASH_POSITIONS_GET = 'investment.cash.get';
export const SETTINGS_VIEW = 'settings.view';
export const SETTINGS_PROFILE = 'settings.profile';
export const SETTINGS_PROFILE_UPDATE = 'settings.profile.update';
export const SETTINGS_PREFERENCES = 'settings.preferences';
export const SETTINGS_PREFERENCES_UPDATE = 'settings.preferences.update';
export const SETTINGS_MFA = 'settings.mfa';
export const SETTINGS_SECURITY = 'settings.security';
export const ADMIN_HUB = 'admin.hub';
export const ADMIN_HUB_PLATFORM = 'admin.hub.platform';
export const ADMIN_HUB_ORGANIZATION = 'admin.hub.organization';
export const ADMIN_USERS_VIEW = 'admin.users.view';
export const ADMIN_USERS_DETAIL = 'admin.users.detail';
export const ADMIN_ROLES_VIEW = 'admin.roles.view';
export const ADMIN_ROLES_DETAIL = 'admin.roles.detail';
export const ADMIN_ORGANIZATION_VIEW = 'admin.organization.view';
export const ADMIN_AUDIT_VIEW = 'admin.audit.view';
export const ADMIN_AUDIT_LOG_LIST = 'admin.auditLog.list';
export const ADMIN_MODULES_LIST = 'admin.modules.list';
export const ADMIN_USER_CREATE = 'admin.user.create';
export const ADMIN_USER_UPDATE = 'admin.user.update';
export const ADMIN_USER_DELETE = 'admin.user.delete';
export const ADMIN_ROLE_CREATE = 'admin.role.create';
export const ADMIN_ROLE_UPDATE = 'admin.role.update';
export const ADMIN_ROLE_DELETE = 'admin.role.delete';
export const DATA_HUB = 'data.hub';
// ──── Access Domain Operations ────
export const ACCESS_MANAGE_MEMBERSHIP = 'access.membership.manage';
export const ACCESS_MANAGE_ROLES = 'access.roles.manage';
export const ACCESS_MANAGE_PERMISSIONS = 'access.permissions.manage';
export const ACCESS_READ_MEMBERS = 'access.members.read';
export const ACCESS_READ_ROLES = 'access.roles.read';
export const ACCESS_READ_PERMISSIONS = 'access.permissions.read';
// ──── Investment Operations ────
export const TRANSACTION_CREATE = 'investment.transaction.create';
export const TRANSACTION_LIST = 'investment.transaction.list';
export const TRANSACTION_EXPORT = 'investment.transaction.export';
export const TRANSACTION_GET = 'investment.transaction.get';
export const TRANSACTION_SUBMIT = 'investment.transaction.submit';
export const TRANSACTION_APPROVE = 'investment.transaction.approve';
export const TRANSACTION_REJECT = 'investment.transaction.reject';
export const TRANSACTION_WITHDRAW = 'investment.transaction.withdraw';
export const TRANSACTION_POST = 'investment.transaction.post';
export const TRANSACTION_REPAIR = 'investment.transaction.repair';
export const TRANSACTION_REPOST = 'investment.transaction.repost';
export const TRANSACTION_REVERSE = 'investment.transaction.reverse';
export const TRANSACTION_LOTS = 'investment.transaction.lots';
export const TRANSACTION_DISPOSALS = 'investment.transaction.disposals';
export const TRANSACTION_FACTS = 'investment.transaction.facts';
export const COMPLIANCE_BREACHES_LIST = 'compliance.breaches.list';
export const COMPLIANCE_BREACHES_GET = 'compliance.breaches.get';
export const COMPLIANCE_BREACHES_OPEN = 'compliance.breaches.open';
export const COMPLIANCE_BREACHES_REMEDIATE = 'compliance.breaches.remediate';
export const COMPLIANCE_BREACHES_WAIVE = 'compliance.breaches.waive';
export const COMPLIANCE_AML_CASES_LIST = 'compliance.aml-cases.list';
export const COMPLIANCE_AML_CASES_GET = 'compliance.aml-cases.get';
export const COMPLIANCE_AML_CASES_OPEN = 'compliance.aml-cases.open';
export const COMPLIANCE_AML_CASES_REVIEW = 'compliance.aml-cases.review';
export const COMPLIANCE_AML_CASES_REPORT = 'compliance.aml-cases.report';
export const COMPLIANCE_AML_CASES_CLOSE = 'compliance.aml-cases.close';
export const RISK_EXPOSURES_LIST = 'risk.exposures.list';
export const INSTRUMENT_LIST = 'investment.instrument.list';
export const INSTRUMENT_GET = 'investment.instrument.get';
export const INSTRUMENT_CREATE = 'investment.instrument.create';
export const INSTRUMENT_UPDATE = 'investment.instrument.update';
export const INSTRUMENT_DELETE = 'investment.instrument.delete';
export const INSTRUMENT_BULK_UPSERT = 'investment.instrument.bulkUpsert';
export const INSTRUMENT_OVERRIDE_CREATE = 'investment.instrument.override.create';
export const INSTRUMENT_OVERRIDE_LIST = 'investment.instrument.override.list';
export const INSTRUMENT_OVERRIDE_UPDATE = 'investment.instrument.override.update';
export const INSTRUMENT_OVERRIDE_DELETE = 'investment.instrument.override.delete';
export const MARKET_DATA_CREATE = 'investment.marketData.create';
export const MARKET_DATA_LIST = 'investment.marketData.list';
export const MARKET_DATA_GET = 'investment.marketData.get';
export const MARKET_DATA_UPDATE = 'investment.marketData.update';
export const MARKET_DATA_DELETE = 'investment.marketData.delete';
export const MARKET_DATA_BULK_UPSERT = 'investment.marketData.bulkUpsert';
export const PORTFOLIO_ACCOUNT_LIST = 'investment.portfolioAccount.list';
export const PORTFOLIO_ACCOUNT_GET = 'investment.portfolioAccount.get';
export const PORTFOLIO_ACCOUNT_GET_BALANCE = 'investment.portfolioAccount.getBalance';
export const PORTFOLIO_ACCOUNT_CREATE = 'investment.portfolioAccount.create';
export const PORTFOLIO_ACCOUNT_UPDATE = 'investment.portfolioAccount.update';
export const PORTFOLIO_ACCOUNT_SIGNATORIES_LIST = 'investment.portfolioAccount.signatories.list';
export const PORTFOLIO_ACCOUNT_SIGNATORY_CREATE = 'investment.portfolioAccount.signatory.create';
export const PORTFOLIO_ACCOUNT_SIGNATORY_DELETE = 'investment.portfolioAccount.signatory.delete';
export const CLIENT_ACCOUNT_LIST = 'platform.clientAccount.list';
export const CLIENT_ACCOUNT_GET = 'platform.clientAccount.get';
export const CLIENT_ACCOUNT_DOCUMENTS_LIST = 'platform.clientAccount.documents.list';
export const CLIENT_ACCOUNT_CREATE = 'platform.clientAccount.create';
export const CLIENT_ACCOUNT_UPDATE = 'platform.clientAccount.update';
export const CLIENT_ACCOUNT_DOCUMENT_CREATE = 'platform.clientAccount.document.create';
export const DATA_PERFORMANCE_LIST = 'investment.performance.list';
export const DATA_PERFORMANCE_EXPORT = 'investment.performance.export';
export const CORPORATE_ACTION_LIST = 'investment.corporateAction.list';
export const CORPORATE_ACTION_GET = 'investment.corporateAction.get';
export const CORPORATE_ACTION_CREATE = 'investment.corporateAction.create';
export const CORPORATE_ACTION_PROCESS = 'investment.corporateAction.process';
export const CORPORATE_ACTION_ENTITLEMENTS_LIST = 'investment.corporateAction.entitlements.list';
export const CORPORATE_ACTION_ELECT = 'investment.corporateAction.elect';
export const CORPORATE_ACTION_ALLOCATE = 'investment.corporateAction.allocate';
export const CORPORATE_ACTION_UPDATE = 'investment.corporateAction.update';
export const CORPORATE_ACTION_SUBMIT = 'investment.corporateAction.submit';
export const CORPORATE_ACTION_APPROVE = 'investment.corporateAction.approve';
export const CORPORATE_ACTION_REJECT = 'investment.corporateAction.reject';
export const CORPORATE_ACTION_CANCEL = 'investment.corporateAction.cancel';
export const CORPORATE_ACTION_EX_DATE_CLASSIFY = 'investment.corporateAction.ex-date.classify';
export const CORPORATE_ACTION_EX_DATE_VERIFY = 'investment.corporateAction.ex-date.verify';
export const FACT_LIST = 'investment.fact.list';
export const FACT_GET = 'investment.fact.get';
export const FACT_POSITION = 'investment.fact.position';
export const FACT_VALUATION = 'investment.fact.valuation';
export const FACT_MOVEMENT = 'investment.fact.movement';
export const FACT_REALIZED = 'investment.fact.realized';
export const FACT_CGT = 'investment.fact.cgt';
export const FACT_NFRS = 'investment.fact.nfrs';
export const FACT_CREATE = 'investment.fact.create';
export const FACT_BATCHES_LIST = 'investment.fact.batches.list';
export const FACT_BATCH_GET = 'investment.fact.batch.get';
export const FACT_BATCH_LINEAGE = 'investment.fact.batch.lineage';
// ──── Accounting Operations ────
export const ACCOUNTING_CHART_CREATE_GROUP = 'accounting.coa.createGroup';
export const ACCOUNTING_CHART_GET_GROUPS = 'accounting.coa.getGroups';
export const ACCOUNTING_CHART_CREATE_ACCOUNT = 'accounting.coa.createAccount';
export const ACCOUNTING_CHART_GET_ACCOUNTS = 'accounting.coa.getAccounts';
export const ACCOUNTING_CHART_GET_ACCOUNT = 'accounting.coa.getAccount';
export const ACCOUNTING_CHART_UPDATE_ACCOUNT = 'accounting.coa.updateAccount';
export const ACCOUNTING_CHART_GET_ACCOUNT_BALANCE = 'accounting.coa.getAccountBalance';
export const ACCOUNTING_FISCAL_YEAR_CREATE = 'accounting.fiscalYear.create';
export const ACCOUNTING_FISCAL_YEAR_LIST = 'accounting.fiscalYear.list';
export const ACCOUNTING_FISCAL_YEAR_GET = 'accounting.fiscalYear.get';
export const ACCOUNTING_FISCAL_YEAR_CLOSE = 'accounting.fiscalYear.close';
export const ACCOUNTING_FISCAL_YEAR_REOPEN = 'accounting.fiscalYear.reopen';
export const ACCOUNTING_FISCAL_PERIODS_GENERATE = 'accounting.fiscalPeriods.generate';
export const ACCOUNTING_FISCAL_PERIOD_CREATE = 'accounting.fiscalPeriod.create';
export const ACCOUNTING_FISCAL_PERIOD_LIST = 'accounting.fiscalPeriod.list';
export const ACCOUNTING_FISCAL_PERIOD_LOCK = 'accounting.fiscalPeriod.lock';
export const ACCOUNTING_FISCAL_PERIOD_UNLOCK = 'accounting.fiscalPeriod.unlock';
export const ACCOUNTING_JOURNAL_CREATE = 'accounting.journal.create';
export const ACCOUNTING_JOURNAL_LIST = 'accounting.journal.list';
export const ACCOUNTING_JOURNAL_GET = 'accounting.journal.get';
export const ACCOUNTING_JOURNAL_POST = 'accounting.journal.post';
export const ACCOUNTING_JOURNAL_REVERSE = 'accounting.journal.reverse';
export const ACCOUNTING_JOURNAL_TEMPLATE_VIEW = 'accounting.journal-template.view';
export const ACCOUNTING_JOURNAL_TEMPLATE_CREATE = 'accounting.journal-template.create';
export const ACCOUNTING_JOURNAL_TEMPLATE_APPROVE = 'accounting.journal-template.approve';
export const ACCOUNTING_JOURNAL_SCHEDULE_VIEW = 'accounting.journal-schedule.view';
export const ACCOUNTING_JOURNAL_SCHEDULE_CREATE = 'accounting.journal-schedule.create';
export const ACCOUNTING_JOURNAL_SCHEDULE_AMEND = 'accounting.journal-schedule.amend';
export const ACCOUNTING_JOURNAL_SCHEDULE_CANCEL = 'accounting.journal-schedule.cancel';
export const ACCOUNTING_JOURNAL_SCHEDULE_APPROVE = 'accounting.journal-schedule.approve';
export const ACCOUNTING_OPENING_CUTOVER_PREVIEW = 'accounting.openingCutover.preview';
export const ACCOUNTING_OPENING_CUTOVER_APPROVE = 'accounting.openingCutover.approve';
export const ACCOUNTING_OPENING_CUTOVER_GET = 'accounting.openingCutover.get';
export const ACCOUNTING_OPENING_CUTOVER_REVERSE = 'accounting.openingCutover.reverse';
export const ACCOUNTING_OPENING_CUTOVER_RESTATE = 'accounting.openingCutover.restate';
export const ACCOUNTING_MANAGEMENT_FEE_LIST = 'accounting.management-fee.list';
export const ACCOUNTING_MANAGEMENT_FEE_GET = 'accounting.management-fee.get';
export const ACCOUNTING_MANAGEMENT_FEE_CALCULATE = 'accounting.management-fee.calculate';
export const ACCOUNTING_MANAGEMENT_FEE_APPROVE = 'accounting.management-fee.approve';
export const ACCOUNTING_MANAGEMENT_FEE_WAIVE = 'accounting.management-fee.waive';
export const ACCOUNTING_MANAGEMENT_FEE_INVOICE = 'accounting.management-fee.invoice';
export const ACCOUNTING_MANAGEMENT_FEE_PAY = 'accounting.management-fee.pay';
export const ACCOUNTING_MANAGEMENT_FEE_CREDIT = 'accounting.management-fee.credit';
export const ACCOUNTING_MANAGEMENT_FEE_REVERSE = 'accounting.management-fee.reverse';
export const ACCOUNTING_PERFORMANCE_FEE_LIST = 'accounting.performance-fee.list';
export const ACCOUNTING_PERFORMANCE_FEE_CALCULATE = 'accounting.performance-fee.calculate';
export const ACCOUNTING_PERFORMANCE_FEE_APPROVE = 'accounting.performance-fee.approve';
export const ACCOUNTING_PERFORMANCE_FEE_CRYSTALLIZE = 'accounting.performance-fee.crystallize';
export const ACCOUNTING_PERFORMANCE_FEE_INVOICE = 'accounting.performance-fee.invoice';
export const ACCOUNTING_PERFORMANCE_FEE_INVOICE_POST = 'accounting.performance-fee.invoice-post';
export const ACCOUNTING_PERFORMANCE_FEE_PAY = 'accounting.performance-fee.pay';
export const ACCOUNTING_PERFORMANCE_FEE_PAYMENT_POST = 'accounting.performance-fee.payment-post';
export const ACCOUNTING_PERFORMANCE_FEE_RESTATE = 'accounting.performance-fee.restate';
export const ACCOUNTING_PERFORMANCE_FEE_RESTATEMENT_REVERSAL_POST = 'accounting.performance-fee.restatement-reversal-post';
export const ACCOUNTING_POSTING_RULE_CREATE = 'accounting.postingRule.create';
export const ACCOUNTING_POSTING_RULE_LIST = 'accounting.postingRule.list';
export const ACCOUNTING_POSTING_RULE_UPDATE = 'accounting.postingRule.update';
export const ACCOUNTING_POSTING_RULE_TEST = 'accounting.postingRule.test';
export const ACCOUNTING_AUTO_POST = 'accounting.autoPost';
export const ACCOUNTING_TRIAL_BALANCE = 'accounting.ledger.trialBalance';
export const ACCOUNTING_GENERAL_LEDGER = 'accounting.ledger.generalLedger';
export const ACCOUNTING_LEDGER_REBUILD_BALANCES = 'accounting.ledger.rebuildBalances';
export const RECONCILIATION_LIST = 'accounting.reconciliation.list';
export const RECONCILIATION_GET = 'accounting.reconciliation.get';
export const RECONCILIATION_CREATE = 'accounting.reconciliation.create';
export const RECONCILIATION_UPDATE = 'accounting.reconciliation.update';
// ──── Reconciliation Operations ────
// External Statement operations
export const RECONCILIATION_STATEMENT_CREATE = 'reconciliation.statement.create';
export const RECONCILIATION_STATEMENT_LIST = 'reconciliation.statement.list';
export const RECONCILIATION_STATEMENT_GET = 'reconciliation.statement.get';
// Reconciliation Run operations
export const RECONCILIATION_RUN_CREATE = 'reconciliation.run.create';
export const RECONCILIATION_RUN_LIST = 'reconciliation.run.list';
export const RECONCILIATION_RUN_GET = 'reconciliation.run.get';
export const RECONCILIATION_RUN_BREAKS_LIST = 'reconciliation.run.breaks.list';
export const RECONCILIATION_RUN_MATCHES_LIST = 'reconciliation.run.matches.list';
export const RECONCILIATION_RUN_CANCEL = 'reconciliation.run.cancel';
export const RECONCILIATION_RUN_RETRY = 'reconciliation.run.retry';
// Reconciliation Break operations
export const RECONCILIATION_BREAK_GET = 'reconciliation.break.get';
export const RECONCILIATION_BREAK_UPDATE = 'reconciliation.break.update';
// Reconciliation Adjustment operations
export const RECONCILIATION_ADJUSTMENT_CREATE = 'reconciliation.adjustment.create';
export const RECONCILIATION_ADJUSTMENT_LIST = 'reconciliation.adjustment.list';
export const RECONCILIATION_ADJUSTMENT_APPROVE = 'reconciliation.adjustment.approve';
export const RECONCILIATION_ADJUSTMENT_REJECT = 'reconciliation.adjustment.reject';
export const RECONCILIATION_ADJUSTMENT_POST = 'reconciliation.adjustment.post';
// Reconciliation Aging/Escalation operations
export const RECONCILIATION_AGING_EXECUTE = 'reconciliation.aging.execute';
export const RECONCILIATION_ESCALATION_EXECUTE = 'reconciliation.escalation.execute';
// ──── Regulatory Operations ────
// Report Definition operations
export const REGULATORY_REPORT_DEFINITION_LIST = 'regulatory.reportDefinition.list';
export const REGULATORY_REPORT_DEFINITION_GET = 'regulatory.reportDefinition.get';
export const REGULATORY_REPORT_DEFINITION_CREATE = 'regulatory.reportDefinition.create';
export const REGULATORY_REPORT_DEFINITION_UPDATE = 'regulatory.reportDefinition.update';
export const REGULATORY_REPORT_DEFINITION_DISABLE = 'regulatory.reportDefinition.disable';
// Report Run operations
export const REGULATORY_REPORT_RUN_GENERATE = 'regulatory.reportRun.generate';
export const REGULATORY_REPORT_RUN_LIST = 'regulatory.reportRun.list';
export const REGULATORY_REPORT_RUN_GET = 'regulatory.reportRun.get';
export const REGULATORY_REPORT_RUN_ARTIFACT_GET = 'regulatory.reportRun.artifact.get';
export const REGULATORY_REPORT_RUN_VALIDATE = 'regulatory.reportRun.validate';
export const REGULATORY_REPORT_RUN_SUBMIT = 'regulatory.reportRun.submit';
export const REGULATORY_REPORT_RUN_EXPORT = 'regulatory.reportRun.export';
export const REGULATORY_REPORT_RUN_CANCEL = 'regulatory.reportRun.cancel';
export const REGULATORY_REPORT_RUN_RETRY = 'regulatory.reportRun.retry';
// Submission operations
export const REGULATORY_SUBMISSION_LIST = 'regulatory.submission.list';
export const REGULATORY_SUBMISSION_ACKNOWLEDGE = 'regulatory.submission.acknowledge';
export const REGULATORY_TAX_REMITTANCE_LIST = 'regulatory.taxRemittance.list';
export const REGULATORY_TAX_REMITTANCE_RECORD = 'regulatory.taxRemittance.record';
export const COMPLIANCE_NEPAL_GENERATE = 'compliance.nepal.generate';
export const COMPLIANCE_NEPAL_EXPORT = 'compliance.nepal.export';
// ──── Tax Rule Operations ────
export const TAX_RULE_LIST = 'organization.tax-rules.list';
export const TAX_RULE_READ = 'organization.tax-rules.read';
export const TAX_RULE_CREATE_VERSION = 'organization.tax-rules.create-version';
export const TAX_RULE_SUBMIT = 'organization.tax-rules.submit';
export const TAX_RULE_APPROVE = 'organization.tax-rules.approve';
export const TAX_RULE_SUSPEND = 'organization.tax-rules.suspend';
export const TAX_RULE_RETIRE = 'organization.tax-rules.retire';
export const TAX_RULE_IMPACT_ANALYZE = 'organization.tax-rules.impact-analyze';
// ──── Import Operations ────
export const IMPORT_LIST = 'import.list';
export const IMPORT_GET = 'import.get';
export const IMPORT_ROWS_LIST = 'import.rows.list';
export const IMPORT_UPLOAD = 'import.upload';
export const IMPORT_COMMIT = 'import.commit';
export const IMPORT_VALIDATE = 'import.validate';
export const IMPORT_RECOVER = 'import.recover';
export const IMPORT_CANCEL = 'import.cancel';
export const IMPORT_RECONCILE = 'import.reconcile';
// ──── All operation IDs for registry
export const ALL_OPERATION_IDS = [
    // Auth
    AUTH_LOGIN,
    AUTH_LOGOUT,
    AUTH_REFRESH,
    AUTH_CHALLENGE_TOTP,
    AUTH_CHALLENGE_RECOVERY,
    AUTH_SELECT_CONTEXT,
    AUTH_PASSWORD_FORGOT,
    AUTH_PASSWORD_RESET,
    AUTH_INVITATION_ACCEPT,
    AUTH_PASSWORD_CHANGE,
    AUTH_SESSION_MANAGE,
    AUTH_SESSION_REVOKE,
    AUTH_ENROLL_MFA,
    AUTH_VERIFY_MFA,
    AUTH_DISABLE_MFA,
    AUTH_READ_PROFILE,
    AUTH_SWITCH_CONTEXT,
    AUTH_ENTER_DELEGATION,
    AUTH_EXIT_DELEGATION,
    // Platform
    PLATFORM_HEALTH_CHECK,
    PLATFORM_HEALTH_READINESS,
    PLATFORM_HEALTH_LIVENESS,
    PLATFORM_METRICS_READ,
    PLATFORM_METRICS_READ_JSON,
    PLATFORM_ORGANIZATIONS_LIST,
    PLATFORM_ORGANIZATIONS_READ,
    PLATFORM_ORGANIZATIONS_CREATE,
    PLATFORM_ORGANIZATIONS_UPDATE,
    PLATFORM_ORGANIZATIONS_DISABLE,
    PLATFORM_ORGANIZATIONS_ONBOARDING_READINESS_READ,
    PLATFORM_ORGANIZATIONS_SUPPORT_COMMAND_ISSUE,
    PLATFORM_ORGANIZATIONS_OFFBOARDING_EXPORT,
    PLATFORM_MODULE_ENABLE,
    PLATFORM_MODULE_DISABLE,
    PLATFORM_ENTITLEMENTS_LIST,
    PLATFORM_MODULES_LIST,
    PLATFORM_MODULES_READ,
    PLATFORM_MODULES_ASSIGN,
    PLATFORM_MODULES_REVOKE,
    PLATFORM_MODULES_CATALOG_LIST,
    PLATFORM_MODULES_CATALOG_MANAGE,
    PLATFORM_MODULES_ASSIGN_TO_ORGANIZATION,
    PLATFORM_MODULES_REVOKE_FROM_ORGANIZATION,
    PLATFORM_REPORT_SNAPSHOT_CREATE,
    PLATFORM_REPORT_RESTATEMENT_CREATE,
    PLATFORM_CONFIGURATION_ROLLOUT_MANAGE,
    PLATFORM_CONFIGURATION_ROLLOUT_READ,
    PLATFORM_DATA_RESIDENCY_READ,
    PLATFORM_DATA_RESIDENCY_POLICY_UPDATE,
    PLATFORM_DATA_RESIDENCY_TRANSFER_EVALUATE,
    PLATFORM_DATA_RESIDENCY_EXCEPTION_APPROVE,
    PLATFORM_JURISDICTION_PACKAGES_READ,
    PLATFORM_JURISDICTION_PACKAGES_MANAGE,
    PLATFORM_REFERENCE_MASTER_READ,
    PLATFORM_REFERENCE_MASTER_MANAGE,
    PLATFORM_MARKET_DATA_QUALITY_READ,
    PLATFORM_MARKET_DATA_QUALITY_MANAGE,
    PLATFORM_MARKET_DATA_QUALITY_APPROVE,
    PLATFORM_PROVIDER_INGESTION_READ,
    PLATFORM_PROVIDER_INGESTION_MANAGE,
    PLATFORM_ISSUER_EVENTS_READ,
    PLATFORM_ISSUER_EVENTS_MANAGE,
    PLATFORM_ISSUER_EVENTS_APPROVE,
    PLATFORM_PERMISSION_CATALOG_MIGRATIONS_READ,
    PLATFORM_PERMISSION_CATALOG_MIGRATIONS_MANAGE,
    PLATFORM_FEDERATION_READ,
    PLATFORM_FEDERATION_MANAGE,
    PLATFORM_SERVICE_ACCOUNTS_READ,
    PLATFORM_SERVICE_ACCOUNTS_MANAGE,
    PLATFORM_ACCESS_REVIEWS_READ,
    PLATFORM_ACCESS_REVIEWS_MANAGE,
    PLATFORM_CRYPTOGRAPHIC_KEYS_READ,
    PLATFORM_CRYPTOGRAPHIC_KEYS_MANAGE,
    PLATFORM_SCHEDULED_CONTROLS_READ,
    PLATFORM_SCHEDULED_CONTROLS_MANAGE,
    PLATFORM_SCHEDULED_CONTROLS_APPROVE,
    PLATFORM_SECURITY_INCIDENTS_READ,
    PLATFORM_SECURITY_INCIDENTS_MANAGE,
    PLATFORM_ORGANIZATION_OWNERSHIP_READ,
    PLATFORM_ORGANIZATION_OWNERSHIP_REQUEST,
    PLATFORM_ORGANIZATION_OWNERSHIP_APPROVE,
    PLATFORM_MIGRATION_GOVERNANCE_READ,
    PLATFORM_MIGRATION_GOVERNANCE_REQUEST,
    PLATFORM_MIGRATION_GOVERNANCE_APPROVE,
    PLATFORM_ORGANIZATION_PORTABILITY_READ,
    PLATFORM_ORGANIZATION_PORTABILITY_REQUEST,
    PLATFORM_ORGANIZATION_PORTABILITY_APPROVE,
    PLATFORM_ORGANIZATION_PORTABILITY_PURGE,
    PLATFORM_NOTIFICATION_GOVERNANCE_READ,
    PLATFORM_NOTIFICATION_GOVERNANCE_MANAGE,
    PLATFORM_NOTIFICATION_GOVERNANCE_APPROVE,
    ORGANIZATION_MODULES_LIST_ASSIGNED,
    ORGANIZATION_MODULES_ENABLE,
    ORGANIZATION_MODULES_DISABLE,
    ORGANIZATION_MODULES_CONFIGURE,
    ORGANIZATION_BRANCHES_LIST,
    ORGANIZATION_BRANCHES_READ,
    ORGANIZATION_BRANCHES_CREATE,
    ORGANIZATION_BRANCHES_UPDATE,
    ORGANIZATION_BRANCHES_DISABLE,
    ORGANIZATION_BRANCHES_MAKE_DEFAULT,
    ORGANIZATION_CLIENTS_LIST,
    ORGANIZATION_CLIENTS_READ,
    ORGANIZATION_CLIENTS_CREATE,
    ORGANIZATION_CLIENTS_UPDATE,
    ORGANIZATION_CLIENTS_DISABLE,
    ORGANIZATION_CLIENT_ACCOUNTS_LIST,
    ORGANIZATION_CLIENT_ACCOUNTS_READ,
    ORGANIZATION_CLIENT_ACCOUNTS_CREATE,
    ORGANIZATION_CLIENT_ACCOUNTS_UPDATE,
    ORGANIZATION_CLIENT_ACCOUNTS_DISABLE,
    ORGANIZATION_CLIENT_ACCOUNT_DOCUMENTS_LIST,
    ORGANIZATION_CLIENT_ACCOUNT_DOCUMENTS_CREATE,
    ORGANIZATION_CLIENT_ACCOUNT_DOCUMENTS_REMOVE,
    ORGANIZATION_CLIENT_ACCOUNT_SIGNATORIES_LIST,
    ORGANIZATION_CLIENT_ACCOUNT_SIGNATORIES_CREATE,
    ORGANIZATION_CLIENT_ACCOUNT_SIGNATORIES_REMOVE,
    ORGANIZATION_PORTFOLIOS_LIST,
    ORGANIZATION_PORTFOLIOS_READ,
    ORGANIZATION_PORTFOLIOS_CREATE,
    ORGANIZATION_PORTFOLIOS_UPDATE,
    ORGANIZATION_PORTFOLIOS_DISABLE,
    ORGANIZATION_BENCHMARK_ASSIGNMENTS_LIST,
    ORGANIZATION_BENCHMARK_ASSIGNMENTS_CREATE,
    ORGANIZATION_BENCHMARK_ASSIGNMENTS_APPROVE,
    CLIENT_DASHBOARD_READ,
    CLIENT_PROFILE_READ,
    CLIENT_PROFILE_UPDATE_LIMITED,
    CLIENT_HOLDINGS_READ_OWN,
    CLIENT_TRANSACTIONS_READ_OWN,
    CLIENT_REPORTS_READ_OWN,
    CLIENT_REPORTS_EXPORT_OWN,
    CLIENT_DOCUMENTS_READ_OWN,
    PLATFORM_LICENSE_CREATE,
    PLATFORM_LICENSE_LIST,
    PLATFORM_SUPPORT_DELEGATIONS_LIST,
    ORGANIZATION_PROFILE_READ,
    ORGANIZATION_PROFILE_DETAIL_READ,
    ORGANIZATION_PROFILE_UPDATE,
    ORGANIZATION_ACCESS_INSPECTOR,
    ORGANIZATION_LEGAL_HOLD_PLACE,
    ORGANIZATION_LEGAL_HOLD_REMOVE,
    ORGANIZATION_LEGAL_HOLD_LIST,
    ORGANIZATION_ADMIN_DELEGATION_CREATE,
    ORGANIZATION_ADMIN_DELEGATION_REVOKE,
    ORGANIZATION_ADMIN_DELEGATION_LIST,
    ORGANIZATION_USERS_UNLOCK,
    ORGANIZATION_USERS_FORCE_PASSWORD_RESET,
    ORGANIZATION_USERS_MFA_REQUIRE_ENROLLMENT,
    ORGANIZATION_USERS_MFA_RESET_ENROLLMENT,
    ORGANIZATION_USERS_MFA_DISABLE,
    ORGANIZATION_USERS_MFA_REGENERATE_RECOVERY,
    NAVIGATION_READ,
    NAVIGATION_HUB,
    NAVIGATION_SHELL_READ,
    NAVIGATION_ROUTES_READ,
    NAVIGATION_ROUTE_READ,
    NAVIGATION_HUB_READ,
    DASHBOARD_VIEW,
    HOLDINGS_VIEW,
    REPORTS_VIEW,
    REPORT_DETAIL_READ,
    RECONCILIATION_VIEW,
    CORPORATE_ACTIONS_VIEW,
    SETTINGS_VIEW,
    SETTINGS_PROFILE,
    SETTINGS_PREFERENCES,
    SETTINGS_MFA,
    SETTINGS_SECURITY,
    ADMIN_HUB,
    ADMIN_HUB_PLATFORM,
    ADMIN_HUB_ORGANIZATION,
    ADMIN_USERS_VIEW,
    ADMIN_USERS_DETAIL,
    ADMIN_ROLES_VIEW,
    ADMIN_ROLES_DETAIL,
    ADMIN_ORGANIZATION_VIEW,
    ADMIN_AUDIT_VIEW,
    ADMIN_AUDIT_LOG_LIST,
    ADMIN_MODULES_LIST,
    ADMIN_USER_CREATE,
    ADMIN_USER_UPDATE,
    ADMIN_USER_DELETE,
    ADMIN_ROLE_CREATE,
    ADMIN_ROLE_UPDATE,
    ADMIN_ROLE_DELETE,
    DATA_HUB,
    // Access
    ACCESS_MANAGE_MEMBERSHIP,
    ACCESS_MANAGE_ROLES,
    ACCESS_MANAGE_PERMISSIONS,
    ACCESS_READ_MEMBERS,
    ACCESS_READ_ROLES,
    ACCESS_READ_PERMISSIONS,
    // Investment
    TRANSACTION_CREATE,
    TRANSACTION_LIST,
    TRANSACTION_EXPORT,
    TRANSACTION_GET,
    TRANSACTION_SUBMIT,
    TRANSACTION_APPROVE,
    TRANSACTION_REJECT,
    TRANSACTION_WITHDRAW,
    TRANSACTION_POST,
    TRANSACTION_REPAIR,
    TRANSACTION_REPOST,
    TRANSACTION_REVERSE,
    TRANSACTION_LOTS,
    TRANSACTION_DISPOSALS,
    TRANSACTION_FACTS,
    COMPLIANCE_BREACHES_LIST,
    COMPLIANCE_BREACHES_GET,
    COMPLIANCE_BREACHES_OPEN,
    COMPLIANCE_BREACHES_REMEDIATE,
    COMPLIANCE_BREACHES_WAIVE,
    COMPLIANCE_AML_CASES_LIST,
    COMPLIANCE_AML_CASES_GET,
    COMPLIANCE_AML_CASES_OPEN,
    COMPLIANCE_AML_CASES_REVIEW,
    COMPLIANCE_AML_CASES_REPORT,
    COMPLIANCE_AML_CASES_CLOSE,
    RISK_EXPOSURES_LIST,
    INSTRUMENT_LIST,
    INSTRUMENT_GET,
    INSTRUMENT_CREATE,
    INSTRUMENT_UPDATE,
    INSTRUMENT_DELETE,
    INSTRUMENT_BULK_UPSERT,
    INSTRUMENT_OVERRIDE_CREATE,
    INSTRUMENT_OVERRIDE_LIST,
    INSTRUMENT_OVERRIDE_UPDATE,
    INSTRUMENT_OVERRIDE_DELETE,
    MARKET_DATA_CREATE,
    MARKET_DATA_LIST,
    MARKET_DATA_GET,
    MARKET_DATA_UPDATE,
    MARKET_DATA_DELETE,
    MARKET_DATA_BULK_UPSERT,
    PORTFOLIO_ACCOUNT_LIST,
    PORTFOLIO_ACCOUNT_GET,
    PORTFOLIO_ACCOUNT_GET_BALANCE,
    PORTFOLIO_ACCOUNT_CREATE,
    PORTFOLIO_ACCOUNT_UPDATE,
    PORTFOLIO_ACCOUNT_SIGNATORIES_LIST,
    PORTFOLIO_ACCOUNT_SIGNATORY_CREATE,
    PORTFOLIO_ACCOUNT_SIGNATORY_DELETE,
    CLIENT_ACCOUNT_LIST,
    CLIENT_ACCOUNT_GET,
    CLIENT_ACCOUNT_DOCUMENTS_LIST,
    CLIENT_ACCOUNT_CREATE,
    CLIENT_ACCOUNT_UPDATE,
    CLIENT_ACCOUNT_DOCUMENT_CREATE,
    CORPORATE_ACTION_LIST,
    CORPORATE_ACTION_GET,
    CORPORATE_ACTION_CREATE,
    CORPORATE_ACTION_UPDATE,
    CORPORATE_ACTION_SUBMIT,
    CORPORATE_ACTION_APPROVE,
    CORPORATE_ACTION_REJECT,
    CORPORATE_ACTION_CANCEL,
    CORPORATE_ACTION_PROCESS,
    CORPORATE_ACTION_ENTITLEMENTS_LIST,
    CORPORATE_ACTION_ELECT,
    CORPORATE_ACTION_ALLOCATE,
    FACT_LIST,
    FACT_GET,
    FACT_POSITION,
    FACT_VALUATION,
    FACT_MOVEMENT,
    FACT_REALIZED,
    FACT_CGT,
    FACT_NFRS,
    FACT_CREATE,
    FACT_BATCHES_LIST,
    FACT_BATCH_GET,
    FACT_BATCH_LINEAGE,
    // Accounting
    ACCOUNTING_CHART_CREATE_GROUP,
    ACCOUNTING_CHART_GET_GROUPS,
    ACCOUNTING_CHART_CREATE_ACCOUNT,
    ACCOUNTING_CHART_GET_ACCOUNTS,
    ACCOUNTING_CHART_GET_ACCOUNT,
    ACCOUNTING_CHART_UPDATE_ACCOUNT,
    ACCOUNTING_CHART_GET_ACCOUNT_BALANCE,
    ACCOUNTING_FISCAL_YEAR_CREATE,
    ACCOUNTING_FISCAL_YEAR_LIST,
    ACCOUNTING_FISCAL_YEAR_GET,
    ACCOUNTING_FISCAL_YEAR_CLOSE,
    ACCOUNTING_FISCAL_YEAR_REOPEN,
    ACCOUNTING_FISCAL_PERIODS_GENERATE,
    ACCOUNTING_FISCAL_PERIOD_CREATE,
    ACCOUNTING_FISCAL_PERIOD_LIST,
    ACCOUNTING_FISCAL_PERIOD_LOCK,
    ACCOUNTING_FISCAL_PERIOD_UNLOCK,
    ACCOUNTING_JOURNAL_CREATE,
    ACCOUNTING_JOURNAL_LIST,
    ACCOUNTING_JOURNAL_GET,
    ACCOUNTING_JOURNAL_POST,
    ACCOUNTING_JOURNAL_REVERSE,
    ACCOUNTING_JOURNAL_TEMPLATE_VIEW,
    ACCOUNTING_JOURNAL_TEMPLATE_CREATE,
    ACCOUNTING_JOURNAL_TEMPLATE_APPROVE,
    ACCOUNTING_JOURNAL_SCHEDULE_VIEW,
    ACCOUNTING_JOURNAL_SCHEDULE_CREATE,
    ACCOUNTING_JOURNAL_SCHEDULE_AMEND,
    ACCOUNTING_JOURNAL_SCHEDULE_CANCEL,
    ACCOUNTING_JOURNAL_SCHEDULE_APPROVE,
    ACCOUNTING_POSTING_RULE_CREATE,
    ACCOUNTING_POSTING_RULE_LIST,
    ACCOUNTING_POSTING_RULE_UPDATE,
    ACCOUNTING_POSTING_RULE_TEST,
    ACCOUNTING_AUTO_POST,
    ACCOUNTING_TRIAL_BALANCE,
    ACCOUNTING_GENERAL_LEDGER,
    ACCOUNTING_LEDGER_REBUILD_BALANCES,
    ACCOUNTING_MANAGEMENT_FEE_LIST,
    ACCOUNTING_MANAGEMENT_FEE_GET,
    ACCOUNTING_MANAGEMENT_FEE_CALCULATE,
    ACCOUNTING_MANAGEMENT_FEE_APPROVE,
    ACCOUNTING_MANAGEMENT_FEE_WAIVE,
    ACCOUNTING_MANAGEMENT_FEE_INVOICE,
    ACCOUNTING_MANAGEMENT_FEE_PAY,
    ACCOUNTING_MANAGEMENT_FEE_CREDIT,
    ACCOUNTING_MANAGEMENT_FEE_REVERSE,
    ACCOUNTING_PERFORMANCE_FEE_LIST,
    ACCOUNTING_PERFORMANCE_FEE_CALCULATE,
    ACCOUNTING_PERFORMANCE_FEE_APPROVE,
    ACCOUNTING_PERFORMANCE_FEE_CRYSTALLIZE,
    ACCOUNTING_PERFORMANCE_FEE_INVOICE,
    ACCOUNTING_PERFORMANCE_FEE_INVOICE_POST,
    ACCOUNTING_PERFORMANCE_FEE_PAY,
    ACCOUNTING_PERFORMANCE_FEE_PAYMENT_POST,
    ACCOUNTING_PERFORMANCE_FEE_RESTATE,
    ACCOUNTING_PERFORMANCE_FEE_RESTATEMENT_REVERSAL_POST,
    RECONCILIATION_LIST,
    RECONCILIATION_GET,
    RECONCILIATION_CREATE,
    RECONCILIATION_UPDATE,
    // Settlement
    SETTLEMENT_INSTRUCTIONS_LIST,
    SETTLEMENT_FAILURES_LIST,
    SETTLEMENT_INSTRUCTIONS_GET,
    SETTLEMENT_INSTRUCTIONS_CREATE,
    SETTLEMENT_INSTRUCTIONS_CONFIRM,
    SETTLEMENT_INSTRUCTIONS_CORRECT,
    SETTLEMENT_INSTRUCTIONS_CANCEL,
    SETTLEMENT_INSTRUCTIONS_AVAILABILITY,
    // Reconciliation
    RECONCILIATION_STATEMENT_CREATE,
    RECONCILIATION_STATEMENT_LIST,
    RECONCILIATION_STATEMENT_GET,
    RECONCILIATION_RUN_CREATE,
    RECONCILIATION_RUN_LIST,
    RECONCILIATION_RUN_GET,
    RECONCILIATION_RUN_BREAKS_LIST,
    RECONCILIATION_RUN_MATCHES_LIST,
    RECONCILIATION_RUN_CANCEL,
    RECONCILIATION_RUN_RETRY,
    RECONCILIATION_BREAK_GET,
    RECONCILIATION_BREAK_UPDATE,
    RECONCILIATION_ADJUSTMENT_CREATE,
    RECONCILIATION_ADJUSTMENT_LIST,
    RECONCILIATION_ADJUSTMENT_APPROVE,
    RECONCILIATION_ADJUSTMENT_REJECT,
    RECONCILIATION_ADJUSTMENT_POST,
    RECONCILIATION_AGING_EXECUTE,
    RECONCILIATION_ESCALATION_EXECUTE,
    // Regulatory
    REGULATORY_REPORT_DEFINITION_LIST,
    REGULATORY_REPORT_DEFINITION_GET,
    REGULATORY_REPORT_DEFINITION_CREATE,
    REGULATORY_REPORT_DEFINITION_UPDATE,
    REGULATORY_REPORT_DEFINITION_DISABLE,
    REGULATORY_REPORT_RUN_GENERATE,
    REGULATORY_REPORT_RUN_LIST,
    REGULATORY_REPORT_RUN_GET,
    REGULATORY_REPORT_RUN_ARTIFACT_GET,
    REGULATORY_REPORT_RUN_VALIDATE,
    REGULATORY_REPORT_RUN_SUBMIT,
    REGULATORY_REPORT_RUN_EXPORT,
    REGULATORY_SUBMISSION_LIST,
    REGULATORY_SUBMISSION_ACKNOWLEDGE,
    REGULATORY_TAX_REMITTANCE_LIST,
    REGULATORY_TAX_REMITTANCE_RECORD,
    COMPLIANCE_NEPAL_GENERATE,
    COMPLIANCE_NEPAL_EXPORT,
    // Tax Rule
    TAX_RULE_LIST,
    TAX_RULE_READ,
    TAX_RULE_CREATE_VERSION,
    TAX_RULE_SUBMIT,
    TAX_RULE_APPROVE,
    TAX_RULE_SUSPEND,
    TAX_RULE_RETIRE,
    TAX_RULE_IMPACT_ANALYZE,
    // Import
    IMPORT_LIST,
    IMPORT_GET,
    IMPORT_ROWS_LIST,
    IMPORT_UPLOAD,
    IMPORT_COMMIT,
    IMPORT_VALIDATE,
    IMPORT_RECOVER,
    IMPORT_CANCEL,
    CORPORATE_ACTION_EX_DATE_CLASSIFY,
    CORPORATE_ACTION_EX_DATE_VERIFY,
    IMPORT_RECONCILE,
];
